import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-mobile-screen',
    title: '1. Native Mobile App Development',
    desc: 'Engineer high-performance native iOS and Android applications utilizing Swift and Kotlin to deliver uncompromised speed, security, and hardware access.',
  },
  {
    icon: 'fa-solid fa-layer-group',
    title: '2. Cross-Platform App Engineering',
    desc: 'Build cost-effective, multi-platform mobile applications from a unified codebase using modern frameworks like React Native and Flutter.',
  },
  {
    icon: 'fa-solid fa-pen-nib',
    title: '3. Human-Centered UI/UX Design',
    desc: 'Craft intuitive, accessible visual design systems, wireframes, and interactive prototypes tailored to maximize user engagement and retention.',
  },
  {
    icon: 'fa-solid fa-globe',
    title: '4. Progressive Web Apps (PWA)',
    desc: 'Develop fast, reliable Progressive Web Applications that deliver native-like mobile experiences directly through modern web browsers.',
  },
  {
    icon: 'fa-solid fa-gauge-high',
    title: '5. Mobile App Performance Optimization',
    desc: 'Audit and optimize startup times, render cycles, battery efficiency, and network payloads to ensure smooth 60fps mobile interaction.',
  },
  {
    icon: 'fa-solid fa-vial-circle-check',
    title: '6. Usability Testing & Accessibility (A11y)',
    desc: 'Conduct rigorous usability testing and enforce WCAG 2.1 compliance to guarantee accessible digital experiences for all users.',
  },
  {
    icon: 'fa-solid fa-plug-circle-bolt',
    title: '7. Mobile API & Backend Integration',
    desc: 'Connect mobile frontends to cloud backends, microservices, payment gateways, and real-time push notification channels via REST and GraphQL.',
  },
  {
    icon: 'fa-solid fa-arrows-rotate',
    title: '8. Legacy App Modernization',
    desc: 'Refactor outdated mobile applications with modern UI/UX design patterns, updated SDKs, and clean modular codebases.',
  },
  {
    icon: 'fa-solid fa-wifi',
    title: '9. Offline-First Architecture & Sync',
    desc: 'Design resilient mobile apps with local database caching and background synchronization to ensure uninterrupted offline functionality.',
  },
  {
    icon: 'fa-solid fa-shield-cat',
    title: '10. Enterprise Mobility Management (EMM)',
    desc: 'Implement secure mobile device management (MDM), biometric authentication, and data encryption policies across corporate fleets.',
  },
];

const whyChooseUs = [
  'User-first methodology prioritizing conversion and retention',
  'Expertise in both Native (Swift/Kotlin) & Cross-Platform (React Native/Flutter)',
  'Strict WCAG 2.1 accessibility and compliance standards',
  'Offline-first mobile architecture with seamless cloud sync',
  'Enterprise-grade security, biometrics, and data encryption',
  'Interactive Figma prototypes before line-of-code execution',
  'Continuous mobile delivery via automated CI/CD pipelines',
  'Comprehensive app store publication and compliance management',
  'Real-time telemetry, crash reporting, and user analytics',
  'Post-launch maintenance and OS compatibility updates',
];

const processSteps = [
  { step: '01', title: 'User Research & Discovery', desc: 'Define target personas, map user journeys, outline feature requirements, and benchmark competitors.' },
  { step: '02', title: 'UX Wireframing & Prototyping', desc: 'Create interactive low/high-fidelity prototypes and conduct usability testing to validate user flows.' },
  { step: '03', title: 'UI Design & Design Systems', desc: 'Craft polished visual interfaces, design tokens, micro-interactions, and component libraries.' },
  { step: '04', title: 'Agile Mobile Development', desc: 'Engineer mobile frontends and backends in rapid sprints with continuous code reviews.' },
  { step: '05', title: 'Quality Assurance & Testing', desc: 'Perform device-matrix testing, automated regression, security audits, and performance tuning.' },
  { step: '06', title: 'Deployment & App Store Operations', desc: 'Manage App Store and Google Play deployments, track live telemetry, and optimize store listings.' },
];

const techStack = [
  { category: 'Mobile Frameworks', items: ['React Native', 'Flutter', 'Swift (iOS)', 'Kotlin (Android)', 'Ionic'] },
  { category: 'UI/UX & Prototyping', items: ['Figma', 'Adobe XD', 'Miro', 'Principle', 'Storybook'] },
  { category: 'Mobile Backend & DBs', items: ['Firebase', 'GraphQL', 'Node.js', 'Realm', 'SQLite'] },
  { category: 'Testing & DevOps', items: ['Appium', 'Detox', 'Fastlane', 'Bitrise', 'Sentry'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const MobilityAndUX = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Digital Transformation
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Mobility & <span className="gradient-text-cyan">UX</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we craft high-impact mobile experiences that users love. Combine human-centered design, robust mobile engineering, and seamless performance to build apps that drive engagement and business growth.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Elevate Your Mobile UX &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Mobility & UX Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Mobility & UX Tech Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Product Design & Engineering Model</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Build Next-Generation Mobile Products with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform your mobile strategy into intuitive, scalable applications designed to captivate users and deliver measurable business outcomes.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Mobile UX Specialist &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MobilityAndUX;